/**
 * sessionSearch — client-side full-text search across the user's conversations.
 *
 * The sidebar used to match titles only, so anything typed into the search box
 * that was not in a conversation title returned nothing. This module searches
 * the whole searchable surface of a session instead:
 *
 *   1. the title
 *   2. the last-message preview
 *   3. every message body (user AND assistant)
 *   4. every attachment name
 *
 * Searchable text is indexed once per session into a lowercase haystack, so a
 * keystroke only costs one `includes` per session; segments are only scanned
 * (and snippets only cut) for the sessions that actually matched.
 *
 * Ranking is "best field first" the way Gmail orders its own results: a hit in
 * the title outranks a hit in the last message, which outranks a hit buried in
 * a message body. Ties break on total hit count, then on recency.
 *
 * This is deliberately the ONLY place that knows how sessions are searched, so
 * swapping the client-side index for a server-side one later means re-implementing
 * `searchSessions` alone.
 */
import type { Message, Session } from "@/modes/ask/types";

/** Content that must never enter the index (placeholders, system notices). */
const IGNORED_CONTENT = new Set(["Thinking...", "Loading..."]);

/** Characters of context kept on each side of a match when cutting a snippet. */
const SNIPPET_RADIUS = 42;

/** Per-session index — built once, then queried on every keystroke. */
export interface SessionSearchDoc {
  session:   Session;
  segments:  SearchSegment[];
  haystack:  string;
}

export type SearchSegmentKind = "title" | "lastMessage" | "message";

export interface SearchSegment {
  kind: SearchSegmentKind;
  text: string;
  /** Set only for "message" segments — lets a result deep-link to that message. */
  messageId?: string;
  role?:      Message["role"];
}

export interface SearchHit {
  segment:      SearchSegment;
  /** Char offset of the first occurrence inside `segment.text`. */
  index:        number;
  /** Occurrences inside that one segment. */
  count:        number;
  /** True when the matched text is the whole segment (a short title). */
  wholeSegment: boolean;
}

export interface SessionSearchResult {
  session:     Session;
  hit:         SearchHit;
  /** Occurrences of the query across the entire session. */
  totalCount:  number;
}

export interface SnippetPart {
  text:  string;
  match: boolean;
}

/** Lower rank sorts first. */
const KIND_RANK: Record<SearchSegmentKind, number> = {
  title:       0,
  lastMessage: 1,
  message:     2,
};

/**
 * Turn stored message HTML (`<br>`, `<p>`, inline tags) into one clean line of
 * text. Also decodes the handful of entities that actually show up in chat
 * content, so searching for "A & B" or "<div>" behaves as typed.
 *
 * Takes `unknown` on purpose: this runs while rendering the sidebar, and a
 * single unexpected value must never be able to throw and blank the whole list.
 */
export function stripHtml(value: unknown): string {
  if (typeof value !== "string") return "";
  return value
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<\/(p|div|li|h[1-6])>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

/** Array fields arrive from Firestore unvalidated — never trust their shape. */
function asArray<T>(value: T[] | undefined | null): T[] {
  return Array.isArray(value) ? value : [];
}

function isSearchable(msg: Message): boolean {
  if (msg.type === "upgrade" || msg.type === "session_completed") return false;
  if (msg.role !== "user" && msg.role !== "assistant") return false;
  return !IGNORED_CONTENT.has(stripHtml(msg.content));
}

/** Index one conversation into searchable segments + a lowercase haystack. */
function buildDoc(session: Session): SessionSearchDoc {
  const segments: SearchSegment[] = [{ kind: "title", text: stripHtml(session.title) }];

  const last = stripHtml(session.lastMessage);
  if (last) segments.push({ kind: "lastMessage", text: last });

  for (const msg of asArray(session.messages)) {
    if (!msg || !isSearchable(msg)) continue;
    const text = stripHtml(msg.content);
    if (text) {
      segments.push({ kind: "message", text, messageId: msg.id, role: msg.role });
    }
    // Attachment names are searchable too — "find that PDF" works.
    for (const att of asArray(msg.attachments)) {
      const name = stripHtml(att?.name);
      if (name) {
        segments.push({ kind: "message", text: name, messageId: msg.id, role: msg.role });
      }
    }
  }

  return {
    session,
    segments,
    haystack: segments.map((s) => s.text).join("\n").toLowerCase(),
  };
}

/** Build the whole index. Memoize on `sessions` — this is the expensive part. */
export function buildSessionDocs(sessions: Session[]): SessionSearchDoc[] {
  return asArray(sessions).filter(Boolean).map(buildDoc);
}

/** Count non-overlapping occurrences of `needle` in `hay`. */
function countIn(hay: string, needle: string): number {
  if (!needle) return 0;
  let count = 0;
  let pos   = 0;
  while ((pos = hay.indexOf(needle, pos)) !== -1) {
    count++;
    pos += needle.length;
  }
  return count;
}

/**
 * Rank `docs` against `query`, best first.
 * `maxResults` caps the returned list; pass 0/undefined for everything.
 */
export function searchSessions(
  docs: SessionSearchDoc[],
  query: string,
  maxResults?: number
): SessionSearchResult[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const results: SessionSearchResult[] = [];

  for (const doc of docs) {
    if (!doc.haystack.includes(q)) continue;

    let best: SearchHit | null = null;
    let bestRank = Number.MAX_SAFE_INTEGER;
    let totalCount = 0;

    for (const segment of doc.segments) {
      const hay = segment.text.toLowerCase();
      const index = hay.indexOf(q);
      if (index === -1) continue;

      const count = countIn(hay, q);
      totalCount += count;

      const rank = KIND_RANK[segment.kind];
      if (rank < bestRank) {
        bestRank = rank;
        best = { segment, index, count, wholeSegment: index === 0 && hay.length === q.length };
      }
    }

    if (best) results.push({ session: doc.session, hit: best, totalCount });
  }

  results.sort((a, b) => {
    const byField = KIND_RANK[a.hit.segment.kind] - KIND_RANK[b.hit.segment.kind];
    if (byField !== 0) return byField;
    if (a.totalCount !== b.totalCount) return b.totalCount - a.totalCount;
    return new Date(b.session.updatedAt).getTime() - new Date(a.session.updatedAt).getTime();
  });

  return typeof maxResults === "number" ? results.slice(0, maxResults) : results;
}

/**
 * Cut a readable snippet centred on the match, split into plain/highlighted
 * parts so the caller can render `<mark>` without `dangerouslySetInnerHTML`.
 */
export function buildSnippet(
  text: string,
  index: number,
  matchLength: number
): SnippetPart[] {
  const start = Math.max(0, index - SNIPPET_RADIUS);
  const end   = Math.min(text.length, index + matchLength + SNIPPET_RADIUS);
  const parts: SnippetPart[] = [];

  if (start > 0) parts.push({ text: "…", match: false });
  if (index > start) parts.push({ text: text.slice(start, index), match: false });
  parts.push({ text: text.slice(index, index + matchLength), match: true });
  if (end > index + matchLength) parts.push({ text: text.slice(index + matchLength, end), match: false });
  if (end < text.length) parts.push({ text: "…", match: false });

  return parts;
}

/** Snippet for a result, ready to render. Empty for an empty query. */
export function snippetForResult(result: SessionSearchResult, query: string): SnippetPart[] {
  const matchLength = query.trim().length;
  if (matchLength === 0) return [];
  return buildSnippet(result.hit.segment.text, result.hit.index, matchLength);
}

/**
 * Split a short label (a conversation title) so every occurrence of `query`
 * is marked. Unlike `buildSnippet` this never elides — labels are shown whole.
 */
export function highlightLabel(text: unknown, query: string): SnippetPart[] {
  const label = typeof text === "string" ? text : "";
  const q = query.trim().toLowerCase();
  if (!q) return [{ text: label, match: false }];

  const hay = label.toLowerCase();
  const parts: SnippetPart[] = [];
  let pos = 0;
  let idx = hay.indexOf(q);

  while (idx !== -1) {
    if (idx > pos) parts.push({ text: label.slice(pos, idx), match: false });
    parts.push({ text: label.slice(idx, idx + q.length), match: true });
    pos = idx + q.length;
    idx = hay.indexOf(q, pos);
  }
  if (pos < label.length) parts.push({ text: label.slice(pos), match: false });

  return parts.length ? parts : [{ text: label, match: false }];
}
